import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { BarChart3, Users, Activity, Eye, ArrowDownRight, ExternalLink, Globe, AlertCircle, RefreshCw, Camera, Database } from 'lucide-react'
import {
  useGa4Status,
  useGa4Performance,
  useGa4Acquisition,
  useGa4TopPages,
  useRefreshGa4Cache,
  type Ga4PropertyMatch,
} from '../hooks/useGa4'
import {
  useTakeGa4Snapshot,
  useGa4Backfill,
  useEnableGa4Snapshots,
} from '../hooks/useGa4Snapshots'

interface GoogleAnalyticsTabProps {
  siteId: string
  domain: string
  tier?: string
}

export function GoogleAnalyticsTab({ siteId, domain, tier }: GoogleAnalyticsTabProps) {
  const { data: status, isLoading: statusLoading, isError, error, refetch } = useGa4Status(siteId)

  if (statusLoading) {
    return (
      <div className="space-y-6">
        {/* Skeleton summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-4 pb-3 px-4">
                <Skeleton className="h-4 w-20 mb-2" />
                <Skeleton className="h-7 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        {/* Skeleton chart */}
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-4 w-28 mb-4" />
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
        {/* Skeleton table */}
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-4 w-24 mb-4" />
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isError) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-destructive mb-4" />
          <h3 className="text-lg font-semibold">Failed to load GA4 data</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            {(error as Error)?.message || 'Could not connect to Google Analytics. The request may have timed out.'}
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (status?.resolving) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <RefreshCw className="mx-auto h-12 w-12 text-muted-foreground mb-4 animate-spin" />
          <h3 className="text-lg font-semibold">Discovering GA4 Property</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Searching connected Google accounts for a GA4 property matching {domain}.
            This happens once and may take up to 30 seconds.
          </p>
        </CardContent>
      </Card>
    )
  }

  if (!status?.connected || status.properties.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <BarChart3 className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold">No GA4 Property Found</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            No Google Analytics 4 property matches {domain}. Connect a Google account with analytics access
            or check that a GA4 property is set up for this domain.
          </p>
          <Button asChild>
            <a href="/dashboard/integrations">Manage Google Connections</a>
          </Button>
        </CardContent>
      </Card>
    )
  }

  return <Ga4DataView siteId={siteId} properties={status.properties} tier={tier} />
}

function Ga4DataView({ siteId, properties, tier }: { siteId: string; properties: Ga4PropertyMatch[]; tier?: string }) {
  const [selectedIdx, setSelectedIdx] = useState(0)
  const [days, setDays] = useState(28)

  const property = properties[selectedIdx] ?? properties[0]
  if (!property) return null
  const propertyId = property.propertyId

  const { data: perf, isLoading: perfLoading } = useGa4Performance(siteId, propertyId, days)
  const { data: acq, isLoading: acqLoading } = useGa4Acquisition(siteId, propertyId, days)
  const { data: topPages, isLoading: pagesLoading } = useGa4TopPages(siteId, propertyId, days)
  const refreshCache = useRefreshGa4Cache(siteId)

  return (
    <div className="space-y-6">
      {/* Property selector (if multiple) */}
      {properties.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          {properties.map((p, i) => (
            <Button
              key={p.propertyId}
              variant={i === selectedIdx ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedIdx(i)}
            >
              {p.displayName}
            </Button>
          ))}
        </div>
      )}

      {/* Connected property info */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <ExternalLink className="h-3.5 w-3.5" />
        <span>{property.displayName}</span>
        <Badge variant="outline" className="text-xs">GA4</Badge>
        <span className="ml-auto flex items-center gap-2">
          <span className="text-xs">{property.email}</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => refreshCache.mutate()}
            disabled={refreshCache.isPending}
            title="Refresh data from Google"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshCache.isPending ? 'animate-spin' : ''}`} />
          </Button>
        </span>
      </div>

      {/* Summary cards */}
      <div className={`grid grid-cols-2 sm:grid-cols-4 gap-4 transition-opacity ${perfLoading ? 'opacity-50 pointer-events-none' : ''}`}>
        <MetricCard
          icon={<Users className="h-5 w-5" />}
          label="Active Users"
          value={perfLoading ? null : perf?.totals.activeUsers.toLocaleString() ?? '0'}
        />
        <MetricCard
          icon={<Activity className="h-5 w-5" />}
          label="Sessions"
          value={perfLoading ? null : perf?.totals.sessions.toLocaleString() ?? '0'}
        />
        <MetricCard
          icon={<Eye className="h-5 w-5" />}
          label="Pageviews"
          value={perfLoading ? null : perf?.totals.pageViews.toLocaleString() ?? '0'}
        />
        <MetricCard
          icon={<ArrowDownRight className="h-5 w-5" />}
          label="Bounce Rate"
          value={perfLoading ? null : perf?.totals.bounceRate != null ? `${(perf.totals.bounceRate * 100).toFixed(1)}%` : '—'}
        />
      </div>

      {/* Date range pills */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Period:</span>
        {[7, 28, 90].map((d) => (
          <Button
            key={d}
            variant={days === d ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDays(d)}
          >
            {d} days
          </Button>
        ))}
      </div>

      {/* Daily trend chart */}
      <Card className={`transition-opacity ${perfLoading ? 'opacity-50 pointer-events-none' : ''}`}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Daily Traffic</CardTitle>
        </CardHeader>
        <CardContent>
          {perfLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : perf && perf.daily.length > 0 ? (
            <DailyChart data={perf.daily} />
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No data for this period.</p>
          )}
        </CardContent>
      </Card>

      {/* Acquisition channels */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Traffic Sources</CardTitle>
        </CardHeader>
        <CardContent>
          {acqLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : acq && acq.channels.length > 0 ? (
            <AcquisitionChart channels={acq.channels} />
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No acquisition data.</p>
          )}
        </CardContent>
      </Card>

      {/* Top pages table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Top Pages</CardTitle>
        </CardHeader>
        <CardContent>
          {pagesLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : topPages && topPages.pages.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 pr-4">Page</th>
                    <th className="pb-2 pr-4 text-right">Pageviews</th>
                    <th className="pb-2 pr-4 text-right">Users</th>
                    <th className="pb-2 pr-4 text-right">Bounce Rate</th>
                    <th className="pb-2 text-right">Avg Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {topPages.pages.map((p) => (
                    <tr key={p.pagePath} className="border-b last:border-0 hover:bg-accent/50">
                      <td className="py-2 pr-4 font-medium max-w-[300px] truncate" title={p.pagePath}>
                        {p.pagePath}
                      </td>
                      <td className="py-2 pr-4 text-right">{p.pageViews.toLocaleString()}</td>
                      <td className="py-2 pr-4 text-right">{p.users.toLocaleString()}</td>
                      <td className="py-2 pr-4 text-right">{(p.bounceRate * 100).toFixed(1)}%</td>
                      <td className="py-2 text-right">{formatDuration(p.avgSessionDuration)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No page data found.</p>
          )}
        </CardContent>
      </Card>

      {/* Snapshot controls (managed tier only) */}
      {tier === 'managed' && <Ga4SnapshotControls siteId={siteId} />}
    </div>
  )
}

function Ga4SnapshotControls({ siteId }: { siteId: string }) {
  const takeSnapshot = useTakeGa4Snapshot(siteId)
  const backfill = useGa4Backfill(siteId)
  const enableSnapshots = useEnableGa4Snapshots(siteId)

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Database className="h-4 w-4" />
          Daily Data Collection
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Store GA4 metrics permanently. GA4 free tier only retains ~2 months of detailed data.
          Daily snapshots preserve your traffic history forever.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => enableSnapshots.mutate()}
            disabled={enableSnapshots.isPending}
          >
            <Camera className="mr-1.5 h-3.5 w-3.5" />
            {enableSnapshots.isPending ? 'Enabling...' : 'Enable Daily Collection'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => takeSnapshot.mutate()}
            disabled={takeSnapshot.isPending}
          >
            <Camera className="mr-1.5 h-3.5 w-3.5" />
            {takeSnapshot.isPending ? 'Taking...' : 'Take Snapshot'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => backfill.mutate()}
            disabled={backfill.isPending}
          >
            <Database className="mr-1.5 h-3.5 w-3.5" />
            {backfill.isPending ? 'Backfilling...' : 'Backfill History (~60 days)'}
          </Button>
        </div>
        {takeSnapshot.isSuccess && (
          <p className="text-sm text-green-600 dark:text-green-400 mt-2">Snapshot taken.</p>
        )}
        {backfill.isSuccess && (
          <p className="text-sm text-green-600 dark:text-green-400 mt-2">
            Backfill complete: {(backfill.data as any)?.totalRows ?? 0} days of data stored.
          </p>
        )}
        {(takeSnapshot.isError || backfill.isError) && (
          <p className="text-sm text-destructive mt-2">
            {(takeSnapshot.error as Error)?.message || (backfill.error as Error)?.message || 'Operation failed.'}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function MetricCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | null }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center gap-2 text-muted-foreground mb-1">
          {icon}
          <span className="text-xs">{label}</span>
        </div>
        {value === null ? (
          <Skeleton className="h-7 w-16" />
        ) : (
          <div className="text-2xl font-bold">{value}</div>
        )}
      </CardContent>
    </Card>
  )
}

function DailyChart({ data }: { data: Array<{ date: string; sessions: number; pageViews: number }> }) {
  const maxSessions = Math.max(...data.map((d) => d.sessions), 1)
  const maxPageViews = Math.max(...data.map((d) => d.pageViews), 1)

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm bg-primary" /> Sessions
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm bg-primary/20" /> Pageviews
        </span>
      </div>
      <div className="flex items-end gap-0.5 h-24" role="img" aria-label="Daily sessions and pageviews chart">
        {data.map((d) => (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-0" title={`${d.date}: ${d.sessions} sessions, ${d.pageViews} pageviews`}>
            <div className="w-full relative" style={{ height: '96px' }}>
              {/* Pageviews bar (background) */}
              <div
                className="absolute bottom-0 w-full bg-primary/15 rounded-t-sm"
                style={{ height: `${(d.pageViews / maxPageViews) * 100}%` }}
              />
              {/* Sessions bar (foreground) */}
              <div
                className="absolute bottom-0 w-full bg-primary rounded-t-sm"
                style={{ height: `${(d.sessions / maxSessions) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>{data[0]?.date.slice(5)}</span>
        <span>{data[data.length - 1]?.date.slice(5)}</span>
      </div>
    </div>
  )
}

function AcquisitionChart({ channels }: { channels: Array<{ channel: string; sessions: number; users: number; engagementRate: number }> }) {
  const maxSessions = Math.max(...channels.map((c) => c.sessions), 1)

  return (
    <div className="space-y-3">
      {channels.map((ch) => (
        <div key={ch.channel} className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Globe className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-medium">{ch.channel}</span>
            </div>
            <div className="flex items-center gap-3 text-muted-foreground text-xs">
              <span>{ch.sessions.toLocaleString()} sessions</span>
              <span>{ch.users.toLocaleString()} users</span>
              <span>{(ch.engagementRate * 100).toFixed(0)}% engaged</span>
            </div>
          </div>
          <div className="h-2 bg-accent rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full"
              style={{ width: `${(ch.sessions / maxSessions) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

/** Format seconds into human-readable duration */
function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`
}
